import {
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { pickSiteWithoutProtectionNoThreatsData } from '@/redux/modules';
import { MONETIZACAO_GATOR_PROTECT_MODAL_NO_THREATS } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { GATOR_PROTECT_ID } from '@/pages/GatorProtect/GatorProtectHandler/GatorProtect.enum';
import {
  EMPTY_GATOR_PROTECT_PRODUCT,
  type ConsultedGatorProduct,
} from '@/pages/GatorProtect/GatorProtectHandler/hooks/gatorProtectConsultedProduct.types';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import {
  isNoThreatsCookieExpired,
  recordNoThreatsCookie,
} from '@/pages/GatorProtect/GatorProtectHandler/utils/gatorProtectCookies';
import type { GPModalsVariants } from '../gatorProtectModalsVisibilityReducer';
import type { HideGatorProtectModalSlot } from '../useGatorProtectModalsVisibility';
import { GP_MODAL_VARIANT_NO_THREATS } from './GatorProtectNoThreatsModal.types';
import type {
  GatorProtectGlobalsDependencies,
  GatorProtectPortalModalsRootState,
} from '../GatorProtectGlobals.types';
import type { GatorProtectModalHookReturn } from '../gatorProtectModalHooks.types';
import { FALLBACK_PORTAL_MODALS } from '../gatorProtectPortalModals.shared';

type PartialProductsReduxState = Partial<{
  products?: {
    consultedProducts?: ConsultedGatorProduct[];
  };
}>;

export type UseGatorProtectNoThreatsModalParams = {
  summary: GatorProtectGlobalsDependencies['summary'];
  isValidPage: boolean;
  hideModalSlot: HideGatorProtectModalSlot;
};

export type UseGatorProtectNoThreatsModalData = {
  noThreatsModalPricing: string | null;
  noThreatsModalPricingIsLoading: boolean;
};

export type UseGatorProtectNoThreatsModalUtils = {
  hideNoThreatsModal: (_topStackModalVariant: GPModalsVariants | null) => void;
};

export type UseGatorProtectNoThreatsModalReturn = GatorProtectModalHookReturn<
  UseGatorProtectNoThreatsModalData,
  UseGatorProtectNoThreatsModalUtils
>;

/**
 * No-threats modal from portal API entry + GrowthBook + weekly cookie cap + checkout PRO pricing footnote.
 * PRO product fetch lives in `useGatorProtectGlobal`.
 * Dismiss-slot visibility is composed in `GatorProtectGlobals`.
 */
export function useGatorProtectNoThreatsModal({
  summary,
  isValidPage,
  hideModalSlot,
}: UseGatorProtectNoThreatsModalParams): UseGatorProtectNoThreatsModalReturn {
  const gatorProtectEnabled = summary.features?.gator_protect ?? false;
  const noThreatsModalFeatureOn = useFeatureIsOn(MONETIZACAO_GATOR_PROTECT_MODAL_NO_THREATS);

  const portalModalsState = useSelector((state: GatorProtectPortalModalsRootState) => state.portalModals);
  const apiModals = portalModalsState ?? FALLBACK_PORTAL_MODALS;

  const consultedProducts = useSelector(
    (state: PartialProductsReduxState) => state.products?.consultedProducts,
  );
  const proProductData = consultedProducts?.find(
    (item: ConsultedGatorProduct) => item.id === GATOR_PROTECT_ID.PRO,
  ) ?? EMPTY_GATOR_PROTECT_PRODUCT;

  const noThreatsModalData = useMemo(() => pickSiteWithoutProtectionNoThreatsData({
    modals: apiModals.modals,
  }), [apiModals.modals]);

  const meetsNoThreatsRules = Boolean(isValidPage
    && gatorProtectEnabled
    && noThreatsModalFeatureOn
    && noThreatsModalData !== null
    && !isNoThreatsCookieExpired());

  const hideNoThreatsModal = useCallback((topStackModalVariant: GPModalsVariants | null) => {
    if (topStackModalVariant !== GP_MODAL_VARIANT_NO_THREATS) {
      return;
    }
    recordNoThreatsCookie();
    hideModalSlot(GP_MODAL_VARIANT_NO_THREATS);
  }, [hideModalSlot]);

  const noThreatsModalPricing = useMemo(() => {
    if (!gatorProtectEnabled) {
      return { displayValue: null as string | null, isFootnoteLoading: false };
    }
    if (Array.isArray(proProductData)) {
      return { displayValue: null as string | null, isFootnoteLoading: true };
    }
    const monthly = proProductData?.prices?.pricing?.monthly;
    if (monthly == null || monthly === '') {
      return { displayValue: null as string | null, isFootnoteLoading: false };
    }
    return {
      displayValue: formatCurrency(monthly),
      isFootnoteLoading: false,
    };
  }, [gatorProtectEnabled, proProductData]);

  return {
    data: {
      noThreatsModalPricing: noThreatsModalPricing.displayValue ?? '',
      noThreatsModalPricingIsLoading: noThreatsModalPricing.isFootnoteLoading,
    },
    utils: {
      hideNoThreatsModal,
    },
    visibilityRule: meetsNoThreatsRules,
  };
}
